import { estadoDeImpago } from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { useBillingPortal, useMe } from '../api/hooks';
import { useToast } from './Toast';

/**
 * Aviso de cobro fallido (mejora 69, Alex, 8 sep 2026).
 *
 * Va arriba del todo y en todas las pantallas porque tiene que ser dificil de
 * no ver, pero NO es un modal: un popup en cada navegacion es una forma
 * rapidisima de que alguien deje de leer lo que dice. Cuenta dos cosas y solo
 * dos: cuanto margen queda y donde se arregla.
 *
 * Y nunca insinua que se vayan a perder datos, porque no se pierden: pasado
 * el margen la cuenta queda en solo lectura y vuelve entera con el cobro.
 */
export function PagoPendienteBanner(): JSX.Element | null {
  const me = useMe();
  const portal = useBillingPortal();
  const toast = useToast();

  const tenant = me.data?.tenant;
  // El cerrojo Founder tiene su propia pantalla: dos avisos de pago a la vez
  // solo consiguen que no se lea ninguno.
  if (!tenant || tenant.founderPending) return null;

  const { estado, diasRestantes } = estadoDeImpago(tenant);
  if (estado === 'al_dia') return null;

  const soloLectura = estado === 'solo_lectura';
  const dias = diasRestantes === 1 ? '1 día' : `${diasRestantes} días`;

  const abrirPortal = () => {
    if (isDemoMode) {
      toast.notify('En modo demostración no hay pasarela de pago conectada.', 'error');
      return;
    }
    portal.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.url;
      },
      onError: (error) => toast.fail(error),
    });
  };

  return (
    <div
      className={`aviso-pago ${soloLectura ? 'aviso-pago--bloqueo' : ''}`.trim()}
      role="status"
      aria-live="polite"
    >
      <p className="aviso-pago__texto">
        {soloLectura ? (
          <>
            <strong>Tu suscripción está pendiente de pago.</strong> Tus datos siguen aquí, pero
            Alexia está temporalmente en modo lectura: puedes consultarlo todo, no agregar ni
            modificar. En cuanto se regularice el pago se reactiva el acceso completo.
          </>
        ) : (
          <>
            <strong>No hemos podido procesar tu pago.</strong> Te {diasRestantes === 1 ? 'queda' : 'quedan'}{' '}
            {dias} para actualizar tu método de pago y mantener el acceso completo a Alexia.
          </>
        )}
      </p>
      <button
        type="button"
        className="btn btn--sm aviso-pago__accion"
        onClick={abrirPortal}
        disabled={portal.isPending}
      >
        {portal.isPending ? 'Abriendo…' : 'Actualizar método de pago'}
      </button>
    </div>
  );
}
